import { CommunityMembershipRole, CommunityMembershipStatus } from '../enums';
import { DomainError } from '../errors/domain-error';
import { grantsCommunityMembershipEntitlement } from '../commerce/community-membership-billing';
import {
  CommunityModeratorPermission,
  hasCommunityModeratorPermission,
} from './community-moderator-permissions.policy';
import {
  containsUnsafeCommunityPostHtml,
  stripCommunityPostMarkdown,
} from './community-post-markdown.policy';

export const COMMUNITY_POST_CATEGORY_MAX_CHARS = 80;
export const COMMUNITY_POST_CONTENT_MAX_CHARS = 10000;

export type CommunityMembershipContext = {
  status: CommunityMembershipStatus | string;
  role: CommunityMembershipRole | string;
  stripeBillingStatus?: string | null;
  graceEndsAt?: Date | string | null;
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: Date | string | null;
  moderatorPermissions?: string[] | null;
  moderatorRolePermissions?: string[] | null;
};

export function isCommunityModeratorRole(role: CommunityMembershipRole | string): boolean {
  return (
    role === CommunityMembershipRole.OWNER || role === CommunityMembershipRole.MODERATOR
  );
}

export function assertCommunityFeedAccess(
  membership: CommunityMembershipContext | null | undefined,
  accessType: string,
): asserts membership is CommunityMembershipContext {
  if (!grantsCommunityMembershipEntitlement(membership, accessType)) {
    throw new DomainError('Community membership required');
  }
}

export function authorizeCommunityPostCreation(
  membership: CommunityMembershipContext | null | undefined,
  accessType: string,
): void {
  assertCommunityFeedAccess(membership, accessType);
}

export function authorizeCommunityPostEdit(params: {
  membership: CommunityMembershipContext;
  userId: string;
  authorId: string;
}): void {
  if (params.userId === params.authorId) return;
  if (hasCommunityModeratorPermission(params.membership, CommunityModeratorPermission.MANAGE_POSTS)) {
    return;
  }
  throw new DomainError('Cannot edit this community post');
}

export function authorizeCommunityPostDeletion(params: {
  membership: CommunityMembershipContext;
  userId: string;
  authorId: string;
}): { isOwner: boolean; isModeratorAction: boolean } {
  const isOwner = params.userId === params.authorId;
  if (isOwner) return { isOwner: true, isModeratorAction: false };
  if (hasCommunityModeratorPermission(params.membership, CommunityModeratorPermission.MANAGE_POSTS)) {
    return { isOwner: false, isModeratorAction: true };
  }
  throw new DomainError('Cannot delete this community post');
}

export function authorizeCommunityPostPin(membership: CommunityMembershipContext): void {
  if (!hasCommunityModeratorPermission(membership, CommunityModeratorPermission.PIN_POSTS)) {
    throw new DomainError('Only community moderators with pin access can pin posts');
  }
}

/** Public social surfaces must exclude community-scoped posts. */
export function isPublicPlatformPost(post: { communityId?: string | null }): boolean {
  return post.communityId == null;
}

export function normalizeCommunityPostCategory(category?: string | null): string | null {
  const trimmed = category?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, COMMUNITY_POST_CATEGORY_MAX_CHARS);
}

export const COMMUNITY_POST_ATTACHMENTS_MAX = 5;
export const COMMUNITY_POST_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const COMMUNITY_POST_SEARCH_MAX_CHARS = 120;

export type CommunityPostAttachment = {
  name: string;
  url: string;
  key?: string | null;
  mimeType: string;
  sizeBytes: number;
};

export function validateCommunityPostSchedule(
  scheduledFor?: Date | string | null,
  now: Date = new Date(),
): { ok: true; value: Date } | { ok: false; message: string } {
  if (!scheduledFor) {
    return { ok: false, message: 'Schedule time is required' };
  }
  const date = scheduledFor instanceof Date ? scheduledFor : new Date(scheduledFor);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, message: 'Invalid schedule time' };
  }
  if (date.getTime() <= now.getTime()) {
    return { ok: false, message: 'Schedule time must be in the future' };
  }
  return { ok: true, value: date };
}

export function resolveCommunityPostCreateStatus(params: {
  publish?: boolean;
  scheduledFor?: Date | null;
}): 'draft' | 'published' {
  if (params.scheduledFor) return 'draft';
  return params.publish === false ? 'draft' : 'published';
}

export function authorizeCommunityDraftAccess(params: {
  membership: CommunityMembershipContext;
  userId: string;
  authorId: string;
}): void {
  if (params.userId === params.authorId) return;
  if (hasCommunityModeratorPermission(params.membership, CommunityModeratorPermission.MANAGE_POSTS)) {
    return;
  }
  throw new DomainError('Cannot access this draft');
}

export const COMMUNITY_POST_SHARE_PREVIEW_MAX_CHARS = 160;

export function validateCommunityQuotePost(params: {
  quotePostId?: string | null;
  type?: string | null;
  pollData?: unknown;
  linkUrl?: string | null;
  mediaUrl?: string | null;
  mediaUrls?: string[] | null;
  attachments?: unknown[] | null;
}): { ok: true } | { ok: false; message: string } {
  if (!params.quotePostId?.trim()) return { ok: true };
  if (params.type === 'poll' || params.pollData != null) {
    return { ok: false, message: 'Quote posts cannot include polls' };
  }
  if (params.linkUrl?.trim()) {
    return { ok: false, message: 'Quote posts cannot include link attachments' };
  }
  if (
    params.mediaUrl?.trim() ||
    (params.mediaUrls?.length ?? 0) > 0 ||
    (params.attachments?.length ?? 0) > 0
  ) {
    return { ok: false, message: 'Quote posts cannot include media attachments' };
  }
  return { ok: true };
}

export function buildCommunityPostSharePreview(params: {
  communityTitle: string;
  authorDisplayName: string;
  postContent: string;
  hasMembership: boolean;
  accessType: string;
  isLevelGated: boolean;
  unlockLevelName?: string | null;
}): { title: string; description: string } {
  const community = params.communityTitle.trim() || 'Circle';
  const author = params.authorDisplayName.trim() || 'Member';

  if (params.accessType === 'paid' && !params.hasMembership) {
    return {
      title: `${community} · LOAMIFY`,
      description: `Join ${community} on LOAMIFY to view member discussions.`,
    };
  }

  if (params.isLevelGated) {
    const levelLabel = params.unlockLevelName?.trim() || 'a higher level';
    return {
      title: `Post in ${community}`,
      description: `Members-only post — unlocks at ${levelLabel}. Join to participate.`,
    };
  }

  const excerpt = stripCommunityPostMarkdown(params.postContent)
    .slice(0, COMMUNITY_POST_SHARE_PREVIEW_MAX_CHARS);
  return {
    title: `${author} in ${community}`,
    description: excerpt || 'Discussion in this community on LOAMIFY.',
  };
}

export function validateCommunityPostMarkdownContent(
  content: string,
): { ok: true } | { ok: false; message: string } {
  if (containsUnsafeCommunityPostHtml(content)) {
    return { ok: false, message: 'Raw HTML is not allowed in community posts' };
  }
  return { ok: true };
}

export function normalizeCommunityPostSearchQuery(query?: string | null): string | null {
  const trimmed = query?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, COMMUNITY_POST_SEARCH_MAX_CHARS);
}

export function isCommunityPostScheduleLocked(
  scheduledFor: Date | string | null | undefined,
  status: string,
  now: Date = new Date(),
): boolean {
  if (status !== 'draft') return false;
  if (!scheduledFor) return false;
  const date = scheduledFor instanceof Date ? scheduledFor : new Date(scheduledFor);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() > now.getTime();
}

export function formatCommunityPostScheduleUnlockLabel(
  scheduledFor: Date | string,
  locale = 'en-US',
): string {
  const date = scheduledFor instanceof Date ? scheduledFor : new Date(scheduledFor);
  const formatted = date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Unlocks on ${formatted}`;
}

export const COMMUNITY_FEED_TYPE_FILTERS = ['audio'] as const;
export type CommunityFeedTypeFilter = (typeof COMMUNITY_FEED_TYPE_FILTERS)[number];

export function normalizeCommunityFeedTypeFilter(
  value?: string | null,
): CommunityFeedTypeFilter | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'audio') return 'audio';
  return null;
}
